import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { FC, useMemo } from 'react'

import {
  DmRoomBoxFragment,
  DmRoomMessageFragment,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { DateTime } from '@/core/entities'

/**
 * DMルームのステータス
 */
enum DmRoomStatus {
  Yet = 'yet',
  Active = 'active',
  Finished = 'finished',
}

type DmRoomStatusTagProps = {
  status: DmRoomStatus
}

const DmRoomStatusTag: FC<DmRoomStatusTagProps> = ({ status }) => {
  const { t } = useTranslation('dmRooms')
  const { bgcolor, color } = useMemo(() => {
    switch (status) {
      case DmRoomStatus.Yet:
        return {
          bgcolor: 'primary.main',
          color: 'white',
        }
      case DmRoomStatus.Active:
        return {
          bgcolor: 'secondary.main',
          color: 'white',
        }
      case DmRoomStatus.Finished:
      default:
        return {
          bgcolor: 'grey.100',
          color: 'text.disabled',
        }
    }
  }, [status])

  return (
    <Chip
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        minWidth: 40,
        bgcolor,
        '.MuiChip-label': {
          px: 1,
        },
      }}
      label={
        <Typography color={color} variant="overline" component="p">
          {t(status)}
        </Typography>
      }
    />
  )
}

type DmRoomBoxProps = {
  dmRoom: DmRoomBoxFragment
  isConsole?: boolean
}

export const DmRoomBox: FC<DmRoomBoxProps> = ({ dmRoom, isConsole }) => {
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('dmRooms')

  /**
   * DMルームのステータスを判定する
   */
  const getDmRoomStatus = (
    startedAt: string,
    expiredAt: string
  ): DmRoomStatus => {
    const now = new Date()
    const startDate = new Date(startedAt)
    const expireDate = new Date(expiredAt)

    if (now < startDate) return DmRoomStatus.Yet
    if (now >= startDate && now < expireDate) return DmRoomStatus.Active
    return DmRoomStatus.Finished
  }

  /**
   * ステータスに応じた日時テキストを取得する
   */
  const getDateText = (
    status: DmRoomStatus,
    startedAt: string,
    expiredAt: string
  ): string | null => {
    if (status === DmRoomStatus.Yet) {
      return t('startFrom', { date: DateTime.format(startedAt) })
    }
    if (status === DmRoomStatus.Active) {
      return t('endAt', { date: DateTime.format(expiredAt) })
    }
    return null
  }

  /**
   * 最新メッセージのテキストを取得する
   */
  const getLatestMessageText = (
    latestMessageNode: DmRoomMessageFragment | null
  ): string => {
    if (!latestMessageNode) return ''

    // 画像メッセージの場合
    if (
      latestMessageNode.type === 'IMAGE' ||
      latestMessageNode.resources?.some(r => r.type === 'IMAGE')
    ) {
      return t('sentImage')
    }

    return latestMessageNode.message ?? ''
  }

  // ステータス判定
  const status = useMemo(
    () => getDmRoomStatus(dmRoom.startedAt, dmRoom.expiredAt),
    [dmRoom.startedAt, dmRoom.expiredAt]
  )

  // 日時表示
  const dateText = useMemo(
    () => getDateText(status, dmRoom.startedAt, dmRoom.expiredAt),
    [status, dmRoom.startedAt, dmRoom.expiredAt]
  )

  // 最新メッセージ
  const latestMessageNode = dmRoom.latestMessage ?? null
  const latestMessageText = useMemo(
    () => getLatestMessageText(latestMessageNode),
    [latestMessageNode]
  )

  // 相対時間
  const relativeTime = useMemo(() => {
    if (!latestMessageNode) return ''
    return latestMessageNode.formattedRelativeCreatedAt ?? ''
  }, [latestMessageNode])

  // ルーム相手のユーザ
  const recipient = useMemo(() => {
    return isConsole ? dmRoom.users[0] : dmRoom.artist
  }, [isConsole, dmRoom.artist, dmRoom.users])

  return (
    <Card elevation={0} sx={{ position: 'relative' }}>
      <CardContent sx={{ py: 2, px: 4, '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DmRoomStatusTag status={status} />
            {dateText && (
              <Typography variant="caption" color="text.secondary">
                {dateText}
              </Typography>
            )}
          </Stack>

          <Typography variant="h6" sx={styleSets.lineCamp(2)}>
            {dmRoom.title}
          </Typography>

          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Avatar
              src={recipient?.avatar?.smallUri ?? undefined}
              sx={{ width: 40, height: 40 }}
            />
            <Stack flex={1} sx={{ minWidth: 0 }}>
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontSize={13}
                    sx={styleSets.lineCamp(1)}
                  >
                    {recipient?.displayName ?? ''}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.hint"
                    sx={{ flexShrink: 0, ml: 1 }}
                  >
                    {relativeTime}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styleSets.lineCamp(1)}
                >
                  {latestMessageText}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
      <CardActionArea
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 1,
        }}
        href={generatePath(
          isConsole
            ? routerPages.ConsoleDmRoomDetail
            : routerPages.DmRoomDetail,
          {
            dmRoomId: dmRoom.id,
          }
        )}
      />
    </Card>
  )
}
